import * as React from "react";

import { AsyncComponent2 } from "@impero-com/common/components/asyncComponent";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { withRouter } from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { emitNotification, FormFrame, PageSpinner } from "@impero-com/ui";
import { NavigateFunction } from "react-router-dom";

import {
    AvailableActions,
    CreateUserGroupData,
    UserTagOverviewListDto as Tags,
} from "api/generated/model";
import { getTags } from "api/generated/tags/tags";
import { postUserGroups } from "api/generated/user-groups/user-groups";
import { getUsers } from "api/generated/users/users";
import { convertUserAndRightsToAdminEditAccessRights } from "utils/accessRights/converters";
import {
    UserAndRights,
    UserGroupAccessRights,
} from "utils/accessRights/models";
import { FormCreate, FormRootComponentProps } from "utils/form";

import { WebRoute } from "../../../../routes";
import { UserGroupTransferValues, UserListResponseOverview } from "../members";

import {
    UserGroupsCreateApplicationBar,
    USER_GROUP_CREATE_DETAILS_STEP,
    USER_GROUP_CREATE_MEMBERS_STEP,
} from "./createAppBar";
import { UserGroupCreatorDetailsStep } from "./stepDetails";
import { UserGroupCreatorMembersStep } from "./stepMembers";

interface UserGroupCreatorFormProps extends FormRootComponentProps<UserGroupCreatorValues> {
    ajaxManager: AjaxManager;
    availableActions: AvailableActions;
    navigate: NavigateFunction;
}

interface UserGroupCreateDetailsFields {
    name: string;
    description: string;
    accessRights: UserAndRights<UserGroupAccessRights>[];
}

interface UserGroupCreatorValues {
    details: UserGroupCreateDetailsFields;
    members: UserGroupTransferValues;
}

interface UserGroupCreatorState {
    status: UserGroupCreatorStatus;
}

type UserGroupCreatorStatus =
    | typeof USER_GROUP_CREATE_DETAILS_STEP
    | typeof USER_GROUP_CREATE_MEMBERS_STEP;

class UserGroupCreatorComp extends React.PureComponent<
    UserGroupCreatorFormProps,
    UserGroupCreatorState
> {
    state: UserGroupCreatorState = {
        status: USER_GROUP_CREATE_DETAILS_STEP,
    };

    render = () => {
        const { availableActions } = this.props;
        const { status } = this.state;
        return (
            <UserGroupsCreateApplicationBar
                currentStepId={status}
                canCreate={availableActions.user.userGroup.create}
            >
                {availableActions.user.userGroup.create ? (
                    this.displayContainer()
                ) : (
                    <NoAccessCard />
                )}
            </UserGroupsCreateApplicationBar>
        );
    };

    displayContainer = () => {
        const { ajaxManager } = this.props;
        return (
            <AsyncComponent2<UserListResponseOverview, Tags>
                promises={[
                    () =>
                        getUsers({ format: "Overview" }).then((response) => {
                            if (response.data.type !== "Overview") {
                                throw new Error(
                                    "Expected an overview user list"
                                );
                            }
                            return { ...response, data: response.data };
                        }),
                    getTags,
                ]}
                renderLoading={() => <PageSpinner />}
                renderError={(code) => <LoggedInHTTPError code={code} />}
                renderContent={(
                    userList: UserListResponseOverview,
                    allTags: Tags
                ) => {
                    return (
                        <FormFrame>
                            {this.displayStep(userList, allTags)}
                        </FormFrame>
                    );
                }}
                key={"CreateMembersComponent"}
                ajaxManager={ajaxManager}
            />
        );
    };

    displayStep = (userList: UserListResponseOverview, allTags: Tags) => {
        const { ajaxManager, form } = this.props;
        const { status } = this.state;
        switch (status) {
            case USER_GROUP_CREATE_DETAILS_STEP:
                return (
                    <UserGroupCreatorDetailsStep
                        onCancel={this.redirectToList}
                        onNext={this.switchToMemberStep}
                        form={form}
                    />
                );
            case USER_GROUP_CREATE_MEMBERS_STEP:
                return (
                    <UserGroupCreatorMembersStep
                        onBack={this.switchToDetailsStep}
                        onNext={() => {
                            this.createGroup();
                        }}
                        onCancel={() => {
                            this.redirectToList();
                        }}
                        form={form}
                        allTags={allTags}
                        userList={userList}
                        ajaxManager={ajaxManager}
                    />
                );
            default:
                return assertNever(status);
        }
    };

    switchToMemberStep = () => {
        this.setState({ status: USER_GROUP_CREATE_MEMBERS_STEP });
    };

    switchToDetailsStep = () => {
        this.setState({ status: USER_GROUP_CREATE_DETAILS_STEP });
    };
    redirectToList = () => {
        this.props.navigate(WebRoute.USER_GROUPS);
    };
    createGroup = () => {
        // Launch the ajax query
        const { form } = this.props;
        const value = form.getFieldsValue();
        if (value.details === undefined || value.members === undefined) {
            return;
        }
        const accessRights = convertUserAndRightsToAdminEditAccessRights(
            value.details.accessRights || []
        );

        const data: CreateUserGroupData = {
            name: value.details.name,
            description: value.details.description,
            accessRights,
            usersGroupUsers: value.members.userGroupUsers || [],
        };
        postUserGroups(data).then((_) => {
            emitNotification("success", t("UserGroup_Create_Success"));
            this.redirectToList();
        });
    };
}

const UserGroupCreator = withRouter(
    FormCreate<UserGroupCreatorValues, UserGroupCreatorFormProps>()(
        UserGroupCreatorComp
    )
);

export {
    UserGroupCreator,
    type UserGroupCreatorValues,
    type UserGroupCreateDetailsFields,
};
